import React from 'react';
import { brand, cta, customerStory, dealers, erp, faqs, founders, heroes, industries, pricing, trust } from '../../content';
import { Arrow, SmartLink, Swipe, cx, useReveal, usePageMeta } from '../../ui';
import { BeforeAfter } from './BeforeAfter';
import { heroUnits } from '../../heroUnits';
import { HeroVisual, QR } from './HeroVisual';
import { HowTabs } from './HowTabs';
import { Check, EmailCapture, H2, Kicker, MicroTrust } from './kit';
import { vbCss } from './styles';

// Hero line: "Know who owns every <noun> you have ever sold." The noun rotates with the hero animation (see
// heroUnits). It names the product category and the pain (you lose sight of the unit after the
// dealer) in eight words, so a cold visitor self-identifies in five seconds. The handover idea is carried
// by the hero animation instead of the headline.
const HERO = heroes.machines;

/* ------------------------------------------------------------------ hero */
function Hero() {
  // The noun follows the product in the hero animation (one per loop), swapped as a cut. Invisible copies of
  // every variant share the h1's grid cell, so the block always has the height of the longest and nothing
  // below it moves.
  const [unitIndex, setUnitIndex] = React.useState(0);
  return (
    <section className="relative overflow-hidden bg-white">
      <div className="wrap vb-hero-wrap pb-14 pt-8 md:pt-12 lg:pb-16">
        <div className="vb-hero-grid">
          <div>
            <SmartLink href="/customers" className="group mb-6 inline-flex max-w-full items-center gap-2.5 rounded-full bg-bm-alt py-1.5 pl-1.5 pr-3.5 text-[13.5px] text-bm-ink ring-1 ring-inset ring-bm-line hover:ring-[#CFCDE4] md:mb-7">
              <span className="whitespace-nowrap rounded-full bg-white px-2 py-0.5 font-mono text-[11px] uppercase tracking-[0.08em] text-bm-emerald ring-1 ring-inset ring-bm-line">
                <span className="vb-pill-long">Customer story</span>
                <span className="vb-pill-short">Story</span>
              </span>
              <span className="truncate">Packing slips first</span>
              <span className="text-bm-muted transition-transform group-hover:translate-x-0.5">
                <Arrow />
              </span>
            </SmartLink>
            <div className="vb-h1-stack">
              {heroUnits.map((u) => (
                <div key={u.key} className="vb-hero-h1 max-w-[780px]" aria-hidden="true" style={{ visibility: 'hidden' }}>
                  Know who owns every <Swipe>{u.noun}</Swipe> you have ever sold.
                </div>
              ))}
              <h1 className="vb-hero-h1 max-w-[780px]">
                Know who owns every <Swipe draw key={unitIndex}>{heroUnits[unitIndex].noun}</Swipe> you have ever sold.
              </h1>
            </div>
            <p className="mt-6 max-w-[480px] text-[17.5px] leading-[1.6] text-bm-muted md:text-[18.5px]">{HERO.sub}</p>
            <EmailCapture source="hero-b" className="mt-7" />
            <MicroTrust />
          </div>
          <div className="vb-hero-panel">
            <div className="vb-dots absolute inset-0 rounded-[26px] bg-bm-alt ring-1 ring-inset ring-bm-line" aria-hidden="true" />
            <div className="relative px-3 pb-5 pt-6 md:px-7 md:pb-6 md:pt-8">
              <HeroVisual splitAt={1100} onUnit={setUnitIndex} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ proof ticker */
const PROOF = [
  ['Customers', brand.socialProof],
  ['ERP', 'Integrated with your systems'],
  ['GDPR', 'DPA built into the terms'],
  ['EU rules', 'Ready for GDPR, Data Act, AI Act and passports'],
  ['Sign-in', 'Passkeys, no passwords'],
  ['Standard', 'GS1 Digital Link'],
  ['Pricing', 'Published, in £ and €'],
  ['Owners', 'No app to install'],
  ['Codes', 'On your domain, under your control'],
];
function ProofTicker() {
  const items = [...PROOF, ...PROOF];
  return (
    <section aria-label="Facts" className="border-b border-bm-line bg-white">
      <div className="vb-ticker py-4">
        <ul className="vb-ticker-track">
          {items.map(([k, v], i) => (
            <li key={i} aria-hidden={i >= PROOF.length} className="flex shrink-0 items-center gap-3 border-r border-bm-line px-7 text-[14px]">
              <span className="vb-label !text-[10.5px]">{k}</span>
              <span className="font-medium text-bm-ink">{v}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ how it works */
function How() {
  return (
    <section id="how" className="section bg-white">
      <div className="wrap">
        <div className="mb-12 grid gap-6 md:mb-14 md:grid-cols-[1.5fr_1fr] md:items-end">
          <div>
            <span className="bar" />
            <H2 lead={<>Tag it. Hand it over. <Swipe>Own it.</Swipe></>} rest="One code does the work." />
          </div>
          <p className="max-w-[440px] text-[17px] leading-[1.6] text-bm-muted md:justify-self-end">
            Three moments, one code on the serial plate or packing slip. Your ERP feeds it; your dealers and owners use it; you see all of it.
          </p>
        </div>
        <HowTabs />
        <InlineCta source="how-b" line="See it on one of your own products." />
      </div>
    </section>
  );
}

function InlineCta({ line, source, dark = false }: { line: string; source: string; dark?: boolean }) {
  return (
    <div className={cx('mt-14 flex flex-col gap-4 rounded-[18px] px-6 py-5 sm:flex-row sm:items-center sm:justify-between md:px-7', dark ? 'bg-white/5 ring-1 ring-inset ring-white/10' : 'bg-bm-alt ring-1 ring-inset ring-bm-line')}>
      <p className={cx('text-[17px] font-semibold tracking-[-0.01em]', dark ? 'text-white' : 'text-bm-indigo')}>{line}</p>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <SmartLink href={`${cta.primaryHref}?src=${source}`} className={cx('btn btn-sm', dark ? 'btn-go' : 'btn-primary')}>
          {cta.primary}
          <Arrow />
        </SmartLink>
        <SmartLink href={cta.secondaryHref} className={cx('text-[14.5px] font-medium underline-offset-4 hover:underline', dark ? 'text-white/75' : 'text-bm-muted')}>
          or {cta.secondary.toLowerCase()}
        </SmartLink>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ industries */
type Vignette = (props: { name: string }) => React.ReactElement;
const chip = 'absolute left-4 bottom-4 flex items-center gap-2 rounded-[12px] bg-white/95 px-3 py-2 text-[12px] shadow-[0_14px_30px_-16px_rgba(24,19,67,.55)] ring-1 ring-black/5';
const ChipCheck = () => (
  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#E3F5E9] text-bm-emerald">
    <Check size={11} />
  </span>
);

const PhotoV = (src: string, alt: string, k: string, v: string): Vignette =>
  function PhotoVignette() {
    return (
      <div className="relative h-full">
        <img src={src} alt={alt} width={640} height={427} loading="lazy" className="h-full w-full object-cover" />
        <div className={chip}>
          <ChipCheck />
          <span className="leading-tight">
            <span className="block font-semibold text-bm-ink">{k}</span>
            <span className="block text-[11px] text-bm-muted">{v}</span>
          </span>
        </div>
      </div>
    );
  };

const SlipV: Vignette = () => (
  <div className="relative flex h-full items-center justify-center bg-bm-alt">
    <div className="absolute inset-0 vb-dots opacity-70" />
    <div className="relative w-[230px] rotate-[-2deg] rounded-[6px] bg-white p-4 shadow-[0_22px_40px_-24px_rgba(24,19,67,.5)] ring-1 ring-black/5">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-bm-muted">Packing slip</div>
          <div className="font-mono text-[12px] font-medium text-bm-ink">PS-10482</div>
        </div>
        <QR size={46} seed={3} fg="#232047" />
      </div>
      {['Lot L24-0917 · 6 pcs', 'Cert. EN 10204 3.1', 'Ship 14 Oct 2026'].map((l) => (
        <div key={l} className="mt-1.5 border-t border-dashed border-bm-line pt-1.5 font-mono text-[9.5px] text-[#4A4766]">
          {l}
        </div>
      ))}
      <div className="mt-2 rounded-[4px] bg-bm-alt px-2 py-1 text-[9.5px] text-bm-ink">Scan to report a problem with this delivery</div>
    </div>
  </div>
);

const ListV = (title: string, rows: [string, string][], foot: string): Vignette =>
  function ListVignette() {
    return (
      <div className="relative flex h-full items-center justify-center bg-bm-alt p-5">
        <div className="absolute inset-0 vb-dots opacity-70" />
        <div className="relative w-full max-w-[280px] rounded-[12px] bg-white p-4 text-[12px] shadow-[0_22px_40px_-24px_rgba(24,19,67,.5)] ring-1 ring-black/5">
          <div className="mb-2 font-semibold text-bm-ink">{title}</div>
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between gap-3 border-b border-[#F0EFF6] py-1.5 last:border-0">
              <span className="text-bm-muted">{k}</span>
              <span className="flex items-center gap-1.5 font-medium text-bm-ink">
                {v}
                <Check size={11} className="text-bm-emerald" />
              </span>
            </div>
          ))}
          <div className="mt-2 rounded-[6px] bg-bm-alt px-2 py-1 text-[10.5px] text-bm-muted">{foot}</div>
        </div>
      </div>
    );
  };

const VIGNETTES: Record<string, Vignette> = {
  'agricultural-machinery': PhotoV('/images/v2/gen/agri-handover-thumb.jpg', 'A dealer technician walking a farmer through a new round baler in a dealer yard', 'PDI signed in the yard', 'Warranty started at handover'),
  'lifting-access-equipment': ListV('Thorough examination', [['Serial', 'MEWP-19-2271'], ['Inspector', 'Signed'], ['Certificate', 'Attached']], 'Next due 14 Jan 2027 · visible to the next hirer'),
  'heating-heat-pumps': PhotoV('/images/v2/gen/heatpump-home-thumb.jpg', 'A homeowner adjusting a round wall controller in a calm hallway', 'Commissioned on site', 'Warranty from 12 Oct 2026'),
  'commercial-kitchen': ListV('Combi oven · service history', [['Gas safety', 'Mar 2026'], ['Annual service', 'Sep 2026'], ['Door seal', 'Replaced']], 'Transferred to the new operator with the site'),
  'industrial-precision': SlipV,
  'premium-durables': PhotoV('/images/v2/gen/hifi-owner-thumb.jpg', 'A hand turning the volume knob of a brushed aluminium amplifier in a living room', 'Second owner registered', 'Service history kept'),
};

function Industries() {
  return (
    <section className="section border-t border-bm-line bg-white">
      <div className="wrap">
        <div className="mb-10 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <H2 className="max-w-[860px]" lead="Built for products that live for decades." rest="And change hands along the way." />
          <SmartLink href="/industries" className="vb-arrow-link shrink-0 text-[15px]">
            All industries <Arrow />
          </SmartLink>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {industries.map((ind) => {
            const V = VIGNETTES[ind.slug];
            return (
              <SmartLink key={ind.slug} href={`/industries/${ind.slug}`} className="vb-card vb-lift group flex flex-col overflow-hidden">
                <div className="aspect-[16/10] overflow-hidden border-b border-bm-line">{V ? <V name={ind.name} /> : null}</div>
                <div className="flex flex-1 items-start justify-between gap-4 p-5">
                  <div>
                    <div className="text-[17px] font-bold tracking-[-0.01em] text-bm-indigo">{ind.name}</div>
                    <div className="mt-1 text-[14.5px] leading-[1.5] text-bm-muted">{ind.short}</div>
                  </div>
                  <span className="mt-1 text-bm-muted transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-bm-indigo">
                    <Arrow />
                  </span>
                </div>
              </SmartLink>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ outcomes by persona */
const Snip = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={cx('rounded-[14px] bg-white p-4 text-[12.5px] shadow-[0_18px_40px_-28px_rgba(24,19,67,.45)] ring-1 ring-bm-line', className)}>{children}</div>
);
const KV = ({ k, v, ok }: { k: string; v: React.ReactNode; ok?: boolean }) => (
  <div className="flex items-center justify-between gap-3 border-b border-[#F0EFF6] py-1.5 last:border-0">
    <span className="text-bm-muted">{k}</span>
    <span className="flex items-center gap-1.5 font-medium text-bm-ink">
      {v}
      {ok && <Check size={12} className="text-bm-emerald" />}
    </span>
  </div>
);

const PERSONAS: { who: string; t: string; d: string; href: string; snip: React.ReactNode }[] = [
  {
    who: 'Aftersales & service',
    t: 'Claims that arrive complete.',
    d: 'Every case is tied to a serial, its owner, its dealer and its history before it reaches your team. Bulletins reach the serials they are about.',
    href: '/platform/service',
    snip: (
      <Snip>
        <div className="mb-2 flex items-center justify-between">
          <span className="font-semibold text-bm-ink">Case 2207</span>
          <span className="rounded-full bg-[#EEF7F1] px-2 py-0.5 text-[11px] font-medium text-[#2F7D4F]">In warranty</span>
        </div>
        <KV k="Unit" v={<span className="font-mono text-[11.5px]">KAW9-26-04182</span>} ok />
        <KV k="Owner" v="14 Orchard Lane" ok />
        <KV k="Photos" v="3 attached" ok />
        <KV k="Routed to" v="Brightwell Heating" ok />
      </Snip>
    ),
  },
  {
    who: 'Managing director',
    t: 'Know who owns what you made.',
    d: 'A live record of every unit in the field, its owner and its dealer. Parts and service revenue you can finally see, instead of guessing.',
    href: '/platform/registration-warranty',
    snip: (
      <Snip>
        <div className="mb-2 font-semibold text-bm-ink">Units by owner</div>
        {[
          ['14 Orchard Lane', 'Halvard AW-9', 'Ludlow'],
          ['Harbour Street Kitchen', 'Fornell CX-10', 'Whitby'],
          ['Ashcombe House', 'Moorcroft RM-42', 'Hexham'],
        ].map(([o, m, p]) => (
          <div key={o} className="flex items-center justify-between border-b border-[#F0EFF6] py-1.5 last:border-0">
            <span className="font-medium text-bm-ink">{o}</span>
            <span className="text-bm-muted">
              {m} · {p}
            </span>
          </div>
        ))}
      </Snip>
    ),
  },
  {
    who: 'Compliance',
    t: 'Same code, two jobs.',
    d: 'The identifier on the plate is GS1 Digital Link on your domain, ready to carry passport data where an EU delegated act requires it. Lot certificates attach at source.',
    href: '/platform/digital-product-passport',
    snip: (
      <Snip>
        <div className="mb-2 flex items-center justify-between">
          <span className="font-semibold text-bm-ink">Product passport</span>
          <span className="inline-flex rounded-full bg-bm-alt p-0.5 text-[11px] ring-1 ring-inset ring-bm-line">
            <span className="rounded-full bg-white px-2 py-0.5 font-medium text-bm-ink shadow-sm">Public</span>
            <span className="px-2 py-0.5 text-bm-muted">Restricted</span>
          </span>
        </div>
        <KV k="Materials" v="Declared" ok />
        <KV k="Lot certificate" v="EN 10204 3.1" ok />
        <KV k="Repair info" v="Linked" ok />
      </Snip>
    ),
  },
  {
    who: 'Marketing',
    t: 'An owner list you actually own.',
    d: 'Owners register in seconds by scanning, under your brand, with consent they chose. Service reminders and offers go to real people, not a dealer’s inbox.',
    href: '/platform/registration-warranty',
    snip: (
      <Snip>
        <div className="mb-1.5 font-semibold text-bm-ink">Keep me updated</div>
        {[
          ['Service reminders', true],
          ['Product recalls', true],
          ['Offers and accessories', false],
        ].map(([l, on]) => (
          <div key={String(l)} className="flex items-center justify-between py-1.5">
            <span className="text-bm-ink">{l}</span>
            <span className={cx('relative h-[18px] w-[30px] rounded-full', on ? 'bg-bm-emerald' : 'bg-[#D9D8E8]')}>
              <span className={cx('absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white shadow', on ? 'left-[14px]' : 'left-[2px]')} />
            </span>
          </div>
        ))}
      </Snip>
    ),
  },
];

function Outcomes() {
  return (
    <section className="section border-t border-bm-line bg-white">
      <div className="wrap">
        <div className="grid gap-6 md:grid-cols-[1.5fr_1fr] md:items-end">
          <H2 lead="One record." rest="Four people who stop chasing paper." />
          <p className="max-w-[460px] text-[17px] leading-[1.6] text-bm-muted md:justify-self-end">
            Forward this to whoever owns the problem. Each of them gets something different from the same code.
          </p>
        </div>
        <div className="mt-12 border-b border-bm-line">
          {PERSONAS.map((p) => (
            <div key={p.who} className="grid gap-5 border-t border-bm-line py-7 md:grid-cols-[200px_1fr_320px] md:gap-10 md:py-8">
              <div className="vb-label pt-1.5">{p.who}</div>
              <div>
                <h3 className="text-[22px] font-bold tracking-[-0.02em] text-bm-indigo md:text-[24px]">{p.t}</h3>
                <p className="mt-2 max-w-[520px] text-[16px] leading-[1.6] text-bm-muted">{p.d}</p>
              </div>
              <div className="md:pt-1">{p.snip}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ dealers */
function Dealers() {
  return (
    <section className="section bg-bm-alt">
      <div className="wrap grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-center lg:gap-16">
        <div>
          <Kicker className="mb-4">{dealers.eyebrow}</Kicker>
          <H2 lead={<>Your dealers stay in <Swipe>every sale.</Swipe></>} />
          <p className="mt-5 max-w-[500px] text-[17px] leading-[1.6] text-bm-muted">{dealers.lede}</p>
          <dl className="mt-8 grid gap-x-8 sm:grid-cols-2">
            {dealers.points.map((pt) => (
              <div key={pt.t} className="border-t border-bm-line py-4">
                <dt className="text-[15.5px] font-semibold text-bm-indigo">{pt.t}</dt>
                <dd className="mt-1 text-[14.5px] leading-[1.55] text-bm-muted">{pt.d}</dd>
              </div>
            ))}
          </dl>
          <SmartLink href="/platform/dealers" className="vb-arrow-link mt-4 text-[15px]">
            What changes for your dealers <Arrow />
          </SmartLink>
        </div>
        <div className="relative mx-auto w-full max-w-[460px]">
          <div className="rounded-[20px] bg-white p-5 shadow-[0_40px_80px_-44px_rgba(24,19,67,.5)] ring-1 ring-bm-line">
            <div className="flex items-center justify-between border-b border-bm-line pb-4">
              <div>
                <div className="vb-label !text-[10px]">Partner view</div>
                <div className="mt-0.5 text-[17px] font-bold tracking-[-0.01em] text-bm-indigo">Brightwell Heating · Ludlow</div>
              </div>
              <span className="grid h-10 w-10 place-items-center rounded-[11px] bg-bm-alt text-[13px] font-bold text-bm-indigo">BH</span>
            </div>
            <ul className="divide-y divide-bm-line">
              {[
                { k: 'Credited', t: 'Halvard AW-9 commissioned at 14 Orchard Lane', s: 'Registered to you at commissioning', tone: 'g' },
                { k: 'Service', t: 'Fan noise reported by owner', s: 'Routed to your engineers, photos attached', tone: 'i' },
                { k: 'Parts', t: 'Fan motor assembly for KAW9-…04163', s: 'Order sent to your parts desk', tone: 'i' },
                { k: 'Resale', t: 'House sold: new owners at 3 Mill Close', s: 'Full service history on one scan', tone: 'm' },
              ].map((r) => (
                <li key={r.t} className="flex items-start gap-3 py-3.5">
                  <span className={cx('mt-0.5 w-[70px] shrink-0 rounded-full px-2 py-0.5 text-center font-mono text-[10px] uppercase tracking-[0.06em]', r.tone === 'g' ? 'bg-[#E3F5E9] text-[#1F7A43]' : r.tone === 'i' ? 'bg-bm-alt text-bm-indigo ring-1 ring-inset ring-bm-line' : 'bg-[#F4F1EA] text-[#6A5A36]')}>
                    {r.k}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[14px] font-semibold leading-snug text-bm-ink">{r.t}</span>
                    <span className="block text-[12.5px] text-bm-muted">{r.s}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ ERP strip */
function Erp() {
  return (
    <section className="border-y border-bm-line bg-white py-16 md:py-20">
      <div className="wrap">
        <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-14">
          <div>
            <Kicker className="mb-4">{erp.eyebrow}</Kicker>
            <H2 lead={<>Your ERP stays <Swipe>the master.</Swipe></>} />
            <p className="mt-5 max-w-[440px] text-[16.5px] leading-[1.6] text-bm-muted">{erp.lede}</p>
            <p className="mt-5 max-w-[440px] border-l-2 border-bm-green pl-4 text-[15px] leading-[1.55] text-bm-ink">{erp.proof}</p>
          </div>
          <div className="overflow-hidden rounded-[18px] ring-1 ring-bm-line">
            {erp.systems.map((s) => (
              <div key={s.name} className="grid gap-2 border-b border-bm-line px-5 py-4 last:border-0 sm:grid-cols-[200px_1fr] sm:gap-6 md:px-6">
                <div className="flex items-center justify-between gap-3 sm:block">
                  <div className="text-[16px] font-bold text-bm-indigo">{s.name}</div>
                  <span className={cx('chip mt-0 sm:mt-1.5', s.status === 'live' && 'chip-live')}>{s.status === 'live' ? (s.name === 'Anything else' ? 'Available' : 'Native, live') : 'Via API today'}</span>
                </div>
                <p className="text-[14.5px] leading-[1.55] text-bm-muted">{s.note}</p>
              </div>
            ))}
            <div className="grid gap-4 bg-bm-alt px-5 py-4 text-[13px] sm:grid-cols-2 md:px-6">
              <div>
                <div className="vb-label mb-1.5 !text-[10px]">Flows in</div>
                <div className="flex flex-wrap gap-1.5">
                  {erp.flows.in.map((f) => (
                    <span key={f} className="rounded-full bg-white px-2.5 py-1 text-bm-ink ring-1 ring-inset ring-bm-line">
                      {f}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="vb-label mb-1.5 !text-[10px]">Flows back</div>
                <div className="flex flex-wrap gap-1.5">
                  {erp.flows.out.map((f) => (
                    <span key={f} className="rounded-full bg-white px-2.5 py-1 text-bm-ink ring-1 ring-inset ring-bm-line">
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ customer story */
function Story() {
  const facts = [customerStory.facts[0], customerStory.facts[1], customerStory.facts[3]];
  return (
    <section className="section bg-white">
      <div className="wrap">
        <div className="overflow-hidden rounded-[24px] ring-1 ring-bm-line">
          <div className="grid lg:grid-cols-[1.25fr_0.75fr]">
            <div className="p-7 md:p-12">
              <Kicker className="mb-6">
                {customerStory.eyebrow} · {customerStory.who}
              </Kicker>
              <blockquote>
                <p className="text-[28px] font-extrabold leading-[1.12] tracking-[-0.03em] text-bm-indigo md:text-[40px]">{customerStory.headline}</p>
                <footer className="mt-4 text-[15px] text-bm-muted">{customerStory.tagline}</footer>
              </blockquote>
              <p className="mt-6 max-w-[580px] text-[16px] leading-[1.65] text-bm-ink">
                They came to look at one of their own-brand machines. They start with a scannable code on every packing slip from their ERP, then add the Digital Product Passport. On the rare day something is not right, their customer scans the slip and the reply starts with the facts.
              </p>
              <dl className="mt-8 grid grid-cols-3 border-t border-bm-line">
                {facts.map((f, i) => (
                  <div key={f.k} className={cx('pt-4', i > 0 && 'border-l border-bm-line pl-4 md:pl-6')}>
                    <dt className="vb-label !text-[10px]">{f.k}</dt>
                    <dd className="mt-1 text-[15px] font-semibold leading-snug text-bm-indigo md:text-[17px]">{f.v}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <SmartLink href={`${cta.primaryHref}?src=story-b`} className="btn btn-primary">
                  {cta.primary} <Arrow />
                </SmartLink>
                <SmartLink href="/customers" className="text-[15px] font-medium text-bm-muted underline-offset-4 hover:text-bm-indigo hover:underline">
                  or read the story
                </SmartLink>
              </div>
            </div>
            <div className="relative min-h-[320px] border-t border-bm-line bg-bm-alt lg:border-l lg:border-t-0">
              <div className="vb-dots absolute inset-0" aria-hidden="true" />
              <div className="relative flex h-full items-center justify-center p-8">
                <div className="w-[260px] rotate-[1.5deg] rounded-[8px] bg-white p-5 shadow-[0_30px_60px_-30px_rgba(24,19,67,.5)] ring-1 ring-black/5">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-bm-muted">Packing slip</div>
                      <div className="font-mono text-[13px] font-medium text-bm-ink">PS-10482</div>
                      <div className="mt-2 font-mono text-[10px] leading-[1.6] text-[#4A4766]">
                        Order SO-55120
                        <br />
                        Lot L24-0917
                        <br />
                        Qty 6 · Cert 3.1
                      </div>
                    </div>
                    <QR size={64} seed={11} fg="#232047" />
                  </div>
                  <div className="mt-4 rounded-[8px] bg-bm-alt p-3">
                    <div className="text-[12px] font-semibold text-bm-ink">Something wrong with this delivery?</div>
                    <div className="mt-0.5 text-[11px] text-bm-muted">Scan to open a case. Order, lot and certificate attached.</div>
                  </div>
                  <div className="mt-3 text-center font-mono text-[9px] uppercase tracking-[0.12em] text-[#A3A1C2]">Illustrative</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ trust */
function Trust() {
  return (
    <section className="section border-t border-bm-line bg-white">
      <div className="wrap">
        <div className="grid gap-6 md:grid-cols-[1.6fr_1fr] md:items-end">
          <div>
            <Kicker className="mb-4">{trust.eyebrow}</Kicker>
            <H2 lead="Your QR codes will outlive any software contract." rest={<>We wrote that <Swipe>down.</Swipe></>} />
          </div>
          <p className="max-w-[420px] text-[16.5px] leading-[1.6] text-bm-muted md:justify-self-end">{trust.lede}</p>
        </div>
        <div className="mt-12 grid border-l border-t border-bm-line sm:grid-cols-2 lg:grid-cols-3">
          {trust.pledges.map((p) => (
            <div key={p.t} className="flex flex-col border-b border-r border-bm-line p-6 md:p-7">
              <h3 className="text-[17px] font-bold leading-snug tracking-[-0.01em] text-bm-indigo">{p.t}</h3>
              <p className="mt-2 flex-1 text-[14.5px] leading-[1.6] text-bm-muted">{p.d}</p>
              <SmartLink href={p.href} className="vb-ext mt-4 font-mono text-[11.5px] uppercase tracking-[0.08em] text-bm-emerald hover:text-bm-indigo">
                {p.src}
              </SmartLink>
            </div>
          ))}
        </div>
        <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <p className="max-w-[720px] text-[14px] leading-[1.6] text-bm-muted">{trust.honest}</p>
          <SmartLink href="/why-trust-us" className="vb-arrow-link shrink-0 text-[15px]">
            Why trust us <Arrow />
          </SmartLink>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ pricing teaser */
function Pricing() {
  return (
    <section className="section bg-bm-alt">
      <div className="wrap">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <H2 lead="Start with one product line." />
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <SmartLink href="/pricing" className="vb-arrow-link text-[15px]">
              Compare plans <Arrow />
            </SmartLink>
            <SmartLink href={cta.priceListHref} className="vb-arrow-link text-[15px]">
              {cta.priceList} <Arrow />
            </SmartLink>
          </div>
        </div>
        <p className="mt-4 max-w-[640px] text-[16.5px] leading-[1.6] text-bm-muted">{pricing.lede}</p>
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {pricing.plans.map((p) => (
            <div key={p.name} className={cx('flex flex-col rounded-[18px] bg-white p-6', p.featured ? 'ring-2 ring-bm-indigo' : 'ring-1 ring-bm-line')}>
              <div className="flex items-center justify-between">
                <span className="text-[15px] font-bold text-bm-indigo">{p.name}</span>
                {p.featured && <span className="rounded-full bg-bm-indigo px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-white">Recommended</span>}
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-[34px] font-extrabold tracking-[-0.03em] text-bm-indigo">{p.price}</span>
                {p.eur && <span className="text-[15px] text-bm-muted">/ {p.eur}</span>}
              </div>
              <div className="text-[13px] text-bm-muted">{p.period}</div>
              <p className="mt-4 border-t border-bm-line pt-4 text-[14.5px] font-medium text-bm-ink">{p.for}</p>
              <ul className="mt-2 flex-1 space-y-1.5 text-[14px] text-bm-muted">
                {p.includes.slice(0, 3).map((x) => (
                  <li key={x} className="flex gap-2">
                    <Check size={14} className="mt-[3px] shrink-0 text-bm-emerald" />
                    {x}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-5 text-[14px] text-bm-muted">{pricing.dealerAddon}</p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ FAQ */
function Faq() {
  const ld = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };
  return (
    <section className="section border-t border-bm-line bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
      <div className="wrap grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
        <div>
          <H2 lead="Questions a careful buyer asks." />
          <p className="mt-5 max-w-[380px] text-[16.5px] leading-[1.6] text-bm-muted">Anything else, ask us on the call. You will talk to a product specialist who knows ERP and aftersales.</p>
          <SmartLink href={`${cta.primaryHref}?src=faq-b`} className="btn btn-primary mt-7">
            {cta.primary} <Arrow />
          </SmartLink>
        </div>
        <div className="vb-faq border-b border-bm-line">
          {faqs.map((f) => (
            <details key={f.q} className="group border-t border-bm-line">
              <summary className="flex items-center justify-between gap-6 py-5 text-[17px] font-semibold tracking-[-0.01em] text-bm-indigo">
                {f.q}
                <span className="vb-plus grid h-8 w-8 shrink-0 place-items-center rounded-full ring-1 ring-inset ring-bm-line" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 12 12">
                    <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </span>
              </summary>
              <p className="max-w-[640px] pb-6 pr-12 text-[16px] leading-[1.65] text-bm-muted">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ final CTA */
function FinalCta() {
  return (
    <section className="on-dark relative overflow-hidden" style={{ background: 'var(--bm-indigo)' }}>
      <div className="wrap grid gap-12 py-20 md:py-28 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
        <div>
          <h2 className="text-[clamp(36px,4.8vw,64px)] font-extrabold leading-[1.02] tracking-[-0.04em] text-white">
            {HERO.title[0]} <Swipe>{HERO.title[1]}</Swipe> {HERO.title[2]}
          </h2>
          <p className="mt-5 max-w-[520px] text-[18px] leading-[1.6] text-white/70">Bring one product. We will show you what its record could look like, live, in 30 minutes. Not a sales script.</p>
          <EmailCapture source="final-b" dark className="mt-8" />
          <MicroTrust dark />
        </div>
        <div className="border-t border-white/15 pt-6 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0">
          <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-bm-lime">{founders.eyebrow}</div>
          <p className="mt-3 text-[19px] font-bold leading-snug tracking-[-0.02em] text-white">We have built the systems we connect to.</p>
          <dl className="mt-5 divide-y divide-white/10 border-y border-white/10">
            {founders.credentials.map((c) => (
              <div key={c.k} className="flex gap-4 py-3">
                <dt className="w-[52px] shrink-0 pt-0.5 font-mono text-[11px] uppercase tracking-[0.1em] text-white/50">{c.k}</dt>
                <dd className="text-[14.5px] leading-[1.5] text-white/80">{c.v}</dd>
              </div>
            ))}
          </dl>
          <SmartLink href={cta.secondaryHref} className="mt-6 inline-flex items-center gap-2 text-[15px] font-semibold text-white hover:text-bm-lime">
            or {cta.secondary.toLowerCase()} first <Arrow />
          </SmartLink>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ page */
export function Home() {
  usePageMeta('BrandedMark | Know who owns every machine you have ever sold', brand.boilerplate);
  const ref = useReveal<HTMLDivElement>();
  return (
    <div ref={ref} className="vb" style={{ overflowX: 'clip' }}>
      <style>{vbCss}</style>
      <Hero />
      <ProofTicker />
      <BeforeAfter />
      <How />
      <Industries />
      <Outcomes />
      <Dealers />
      <Erp />
      <Story />
      <Trust />
      <Pricing />
      <Faq />
      <FinalCta />
    </div>
  );
}
